import { useEffect, useState } from "react";
import {
  AlertTriangleIcon,
  Code,
  PlusIcon,
  XIcon,
} from "lucide-react";
import { Input } from "@component/input";
import { Textarea } from "@component/textarea";
import { Button } from "@component/button";
import { Project } from "@type/cv";
import { cn } from "@util/tailwind";

interface Props {
  isEditing: boolean;
  isDisabled: boolean;
  data: Project[];
  onChange: (values: Project[]) => void;
  onEdit: () => void;
}

export function Projects(props: Props) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [newTech, setNewTech] = useState("");

  useEffect(() => {
    setProjects(props.data);
  }, []);

  useEffect(() => {
    props.onChange(projects);
  }, [projects]);

  const handleProjectChange = (index: number, field: keyof Project, value: string) => {
    const updatedProjects = [...projects];
    // Type assertion to any to avoid type errors when assigning string to different field types
    (updatedProjects[index][field] as any) = value;
    setProjects(updatedProjects);
  };

  const addTechnology = (index: number) => {
    if (newTech.trim()) {
      const updatedProjects = [...projects];
      updatedProjects[index].tech_stack.push(newTech.trim());
      setProjects(updatedProjects);
      setNewTech("");
    }
  };

  const removeTechnology = (projectIndex: number, techIndex: number) => {
    const updatedProjects = [...projects];
    updatedProjects[projectIndex].tech_stack.splice(techIndex, 1);
    setProjects(updatedProjects);
  };

  const addProject = () => {
    setProjects([
      ...projects,
      {
        name: "",
        description: "",
        tech_stack: [],
        github_link: "",
        live_demo: "",
        role: "",
        year: "",
      },
    ]);
  };

  const removeProject = (index: number) => {
    setProjects(projects.filter((_, i) => i !== index));
  };

  return (
    <div className="p-6">
      <div>
        {!props.isEditing && projects.length === 0 && (
          <Button
            onClick={props.onEdit}
            disabled={props.isDisabled}
            variant="ghost"
            className="flex items-center text-blue-500 hover:text-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Project
          </Button>
        )}

        {projects.map((project, index) => (
          <div
            key={index}
            className="overflow-hidden"
          >
            {props.isEditing ? (
              <div className="space-y-4 mb-6" id={`q2veu3_${index}`}>
                <button
                  onClick={() => removeProject(index)}
                  className="float-right text-red-500 hover:text-red-700"
                  id={`gz2pka_${index}`}
                >
                  <XIcon className="h-5 w-5" id={`ms56wm_${index}`} />
                </button>

                <div id={`8th3fu_${index}`}>
                  <label
                    className="text-sm text-black mb-1 block"
                    id={`o12ujw_${index}`}
                  >
                    Project Title
                  </label>
                  <Input
                    value={project.name}
                    onChange={(e) =>
                      handleProjectChange(index, "name", e.target.value)
                    }
                    className="border-gray-300 text-black"
                    id={`8v55fm_${index}`}
                  />
                  {project.name === "" && (
                    <div className="flex flex-row items-center mt-2 space-x-1">
                      <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                      <p className="text-red-600 text-sm">
                        Project title is required.
                      </p>
                    </div>
                  )}
                </div>

                <div
                  className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  id={`s9lgx7_${index}`}
                >
                  <div id={`65omex_${index}`}>
                    <label
                      className="text-sm text-black mb-1 block"
                      id={`1kw1xv_${index}`}
                    >
                      Role
                    </label>
                    <Input
                      value={project.role}
                      onChange={(e) =>
                        handleProjectChange(index, "role", e.target.value)
                      }
                      className="border-gray-300 text-black"
                      id={`9e4a44_${index}`}
                    />
                    {project.role === "" && (
                      <div className="flex flex-row items-center mt-2 space-x-1">
                        <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                        <p className="text-red-600 text-sm">
                          Role is required.
                        </p>
                      </div>
                    )}
                  </div>
                  <div id={`ur54uu_${index}`}>
                    <label
                      className="text-sm text-black mb-1 block"
                      id={`gtorql_${index}`}
                    >
                      Year
                    </label>
                    <Input
                      value={project.year}
                      onChange={(e) =>
                        handleProjectChange(index, "year", e.target.value)
                      }
                      className="border-gray-300 text-black"
                      id={`ttpt6d_${index}`}
                    />
                    {project.year === "" && (
                      <div className="flex flex-row items-center mt-2 space-x-1">
                        <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                        <p className="text-red-600 text-sm">
                          Year is required.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div id={`gumrp4_${index}`}>
                  <label
                    className="text-sm text-black mb-1 block"
                    id={`seifbf_${index}`}
                  >
                    Description
                  </label>
                  <Textarea
                    value={project.description}
                    onChange={(e) =>
                      handleProjectChange(index, "description", e.target.value)
                    }
                    className="border-gray-300 text-black"
                    rows={3}
                    id={`adslkv_${index}`}
                  />
                </div>

                <div
                  className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  id={`rb8gvh_${index}`}
                >
                  <div id={`4jhoww_${index}`}>
                    <label
                      className="text-sm text-black mb-1 block"
                      id={`cxin7e_${index}`}
                    >
                      GitHub Link
                    </label>
                    <Input
                      value={project.github_link}
                      onChange={(e) =>
                        handleProjectChange(
                          index,
                          "github_link",
                          e.target.value
                        )
                      }
                      className="border-gray-300 text-black"
                      id={`5rwu6b_${index}`}
                    />
                  </div>
                  <div id={`hjh89e_${index}`}>
                    <label
                      className="text-sm text-black mb-1 block"
                      id={`cmlxxb_${index}`}
                    >
                      Live Demo URL
                    </label>
                    <Input
                      value={project.live_demo}
                      onChange={(e) =>
                        handleProjectChange(index, "live_demo", e.target.value)
                      }
                      className="border-gray-300 text-black"
                      id={`8wb07x_${index}`}
                    />
                  </div>
                </div>

                <div id={`mgifwe_${index}`}>
                  <label
                    className="text-sm text-black mb-1 block"
                    id={`qjvr5r_${index}`}
                  >
                    Technologies
                  </label>
                  <div
                    className="flex flex-wrap gap-2 mb-2"
                    id={`ed745n_${index}`}
                  >
                    {project.tech_stack.map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                      >
                        {tech}
                        <button
                          onClick={() => removeTechnology(index, techIndex)}
                          className="ml-2 text-gray-500 hover:text-gray-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2" id={`el7nxo_${index}`}>
                    <Input
                      value={newTech}
                      onChange={(e) => setNewTech(e.target.value)}
                      placeholder="Add a technology"
                      className="border-gray-300 text-black"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTechnology(index);
                        }
                      }}
                      id={`sy47jl_${index}`}
                    />

                    <Button
                      onClick={() => addTechnology(index)}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      id={`pwztll_${index}`}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div key={index} className={cn("relative pl-12", {
                "pb-8": index < projects.length - 1,
              })}>
                {/* Timeline line - only show for non-last items */}
                {index < projects.length - 1 && (
                  <div
                    className="absolute left-3 top-1 bottom-0 w-[2px]"
                    style={{ backgroundColor: "#f3e8ff" }}
                  ></div>
                )}
                {/* Icon */}
                <div className="absolute left-0 top-1">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center z-10 border-2"
                    style={{
                      backgroundColor: "#f3e8ff",
                      border: "1px solid #7c3aed",
                    }}
                  >
                    <Code
                      className="h-3 w-3"
                      style={{ color: "#9333ea" }}
                    />
                  </div>
                </div>
                {/* Content */}
                <div>
                  <div className="flex sm:flex-row flex-col flex-wrap items-start gap-2 mb-1 relative">
                    <h5 className="font-medium text-gray-900 dark:text-gray-100 w-full text-left ">
                      {project.name}
                      {" "}
                      {project.role && (
                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded-full">
                          {project.role}
                        </span>
                      )}
                    </h5>
                    {project.year && (
                      <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">
                        {project.year}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {project.description}
                  </p>
                  {/* Technologies */}
                  {project.tech_stack.length > 0 && (
                    <div className="mb-3 flex flex-row flex-wrap items-center gap-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Technologies:
                      </p>
                      {project.tech_stack.map(
                        (tech, techIndex) => (
                          <span key={techIndex} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 rounded-md">
                            {tech}
                          </span>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {props.isEditing && (
        <Button
          onClick={addProject}
          variant="ghost"
          className="mt-4 flex items-center text-blue-500 hover:text-blue-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Project
        </Button>
      )}
    </div>
  );
}
